import React, { useState, useRef, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Drawer, Button, Grid, toast } from '@lionstep/ui';

// Selectors
import { extraInfoSelector } from '../../../modules/job/redux/selectors/job.selectors';
import { killerQuestionsSelector } from '../../../modules/job/redux/selectors/adminActions.selectors';

// Actions
import {
  updateExtraInfo,
  updateKillerQuestions,
} from '../../../modules/job/redux/actions';

// Queries
import {
  useQuestionsQuery,
  useQuestionsMutation,
} from '../../../queries/jobQueries';
import { useGenerateTextMutation } from './generateText.queries';

// Components
import GenerateTextForm from './GenerateTextForm';
import GeneratedTextDisplay from './GeneratedTextDisplay';
import GeneratedQuestionsDisplay from './GeneratedQuestionsDisplay';

// Styles
import styles from './generateTextDrawer.module.scss';

const GenerateTextDrawer = ({ show, onClose, onSuccess }) => {
  const { id: jobId } = useParams();

  // Redux
  const dispatch = useDispatch();
  const extraInfo = useSelector(extraInfoSelector);
  const killerQuestions = useSelector(killerQuestionsSelector);

  // State
  const [generatedText, setGeneratedText] = useState(null);
  const formRef = useRef();

  // Queries
  const { data: optionalQuestions } = useQuestionsQuery(
    {
      id: jobId,
      questionType: 'optional_questions',
    },
    { enabled: false },
  );
  const { mutate: generateText, isLoading: isGeneratingText } =
    useGenerateTextMutation({
      jobId,
      onSuccess: (data) => {
        setGeneratedText(data);
      },
      onError: () => toast.error('Error generating text, please try again.'),
    });
  const { mutate: updateOptionalQuestions } = useQuestionsMutation({
    id: jobId,
    questionType: 'optional_questions',
  });

  const handleFormSubmit = (data) => {
    generateText(data);
  };

  const handleClose = () => {
    setGeneratedText(null);
    onClose();
  };

  const handleButtonClick = () => {
    if (!generatedText && formRef.current) {
      formRef.current.submit();
    } else if (generatedText) {
      const extraInfoData = generatedText.texts
        ? {
            introduction: generatedText.texts.introduction,
            summary: generatedText.texts.summary,
            your_role: generatedText.texts.role_tasks,
            requirements: generatedText.texts.must_have,
            nice_to_have: generatedText.texts.nice_to_have,
            team_desc: generatedText.texts.team,
            // additional_benefits: generatedText.texts.additional_benefits,
            searcher_info: generatedText.texts.searcher_info,
          }
        : null;
      const optionalQuestionsData =
        generatedText.questions.optional_questions?.map((question) => ({
          choices: question.choices,
          hide: question.hide,
          is_active: question.is_active,
          question_id: question.question_id,
          question_type: question.question_type,
          sort_order: question.sort_order,
          title: question.title,
        }));
      const killerQuestionsData = generatedText.questions.killer_questions?.map(
        (question) => ({
          choices: question.choices,
          hide: question.hide,
          question_id: question.question_id,
          question_type: question.question_type,
          sort_order: question.sort_order,
          title: question.title,
        }),
      );

      const promises = [];
      if (extraInfoData) {
        promises.push(dispatch(updateExtraInfo(jobId, extraInfoData)));
      }

      if (optionalQuestionsData.length) {
        promises.push(
          updateOptionalQuestions({
            data: { questions: optionalQuestionsData },
          }),
        );
      }
      if (killerQuestionsData.length) {
        promises.push(
          dispatch(
            updateKillerQuestions({
              id: jobId,
              data: killerQuestionsData,
            }),
          ),
        );
      }

      Promise.allSettled(promises).then(() =>
        onSuccess({
          extraInfoData,
          optionalQuestionsData,
          killerQuestionsData,
        }),
      );
    }
  };

  const getButtonText = () => {
    if (!generatedText) {
      return isGeneratingText ? 'Generating...' : 'Start';
    }
    return 'Save';
  };

  // Lock background scroll when drawer is open
  useEffect(() => {
    // Store the current scroll position
    const { scrollY } = window;

    if (show) {
      // Lock the body scroll
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.width = '100%';
    }

    return () => {
      // Restore scroll when drawer closes
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';
      window.scrollTo(0, scrollY);
    };
  }, [show]);

  return (
    <Drawer
      show={show}
      onClose={onClose}
      className={styles.drawer}
      width={generatedText ? 800 : 450}
    >
      <div className={styles.container}>
        <div className={styles.header}>Generate Text Sections</div>
        <div className={styles.body}>
          <Grid.Container>
            {!generatedText ? (
              <GenerateTextForm
                ref={formRef}
                onSubmit={handleFormSubmit}
                isGeneratingText={isGeneratingText}
              />
            ) : (
              <>
                <GeneratedTextDisplay
                  extraInfo={extraInfo}
                  generatedText={generatedText}
                  killerQuestions={killerQuestions}
                  optionalQuestions={optionalQuestions}
                />
                <GeneratedQuestionsDisplay
                  killerQuestions={killerQuestions}
                  optionalQuestions={optionalQuestions}
                  generatedText={generatedText}
                />
              </>
            )}
          </Grid.Container>
        </div>
        <div className={styles.footer}>
          <Button
            className={styles.footerButton}
            ghost
            disabled={isGeneratingText}
            onClick={handleClose}
          >
            Cancel
          </Button>
          <Button
            disabled={isGeneratingText}
            loading={isGeneratingText}
            className={styles.footerButton}
            onClick={handleButtonClick}
          >
            {getButtonText()}
          </Button>
        </div>
      </div>
    </Drawer>
  );
};

export default GenerateTextDrawer;
